"use client";
import { useRouter, useSearchParams } from "next/navigation";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa"; // ✅ Icônes

type PaginationProps = {
  currentPage: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  showIcons?: boolean;
  previousLabel?: string;
  nextLabel?: string;
};

export default function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  showIcons = true,
  previousLabel = "Précédent",
  nextLabel = "Suivant",
}: PaginationProps) {
  const router = useRouter();
  const searchParams = useSearchParams();

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      onPageChange(page);
      router.push(`?page=${page}`);
    }
  };

  // ✅ Générer la pagination dynamique
  const getPagination = () => {
    const pages = [];
    const maxPagesToShow = 5; // Nombre max de pages visibles

    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, "...", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages,
        );
      }
    }

    return pages;
  };

  return (
    <div className="mt-4 flex justify-center space-x-2">
      {/* Bouton Précédent */}
      <button
        className="ml-2 flex items-center rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-600 shadow-sm transition-all hover:border-slate-800 hover:bg-slate-800 hover:text-white hover:shadow-lg disabled:pointer-events-none disabled:opacity-50"
        onClick={() => goToPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        {showIcons ? <FaAngleLeft size={16} /> : previousLabel}
      </button>

      {/* Pages dynamiques */}
      {getPagination().map((page, index) =>
        typeof page === "number" ? (
          <button
            key={index}
            className={`ml-2 min-w-9 rounded-full border px-3.5 py-2 text-center text-sm shadow-sm transition-all ${
              currentPage === page
                ? "border-transparent bg-slate-800 text-white shadow-md hover:bg-slate-700"
                : "border-slate-300 text-slate-600 hover:border-slate-800 hover:bg-slate-800 hover:text-white"
            }`}
            onClick={() => goToPage(page)}
          >
            {page}
          </button>
        ) : (
          <span key={index} className="px-2 text-slate-500">
            ...
          </span>
        ),
      )}

      {/* Bouton Suivant */}
      <button
        className="ml-2 flex items-center rounded-full border border-slate-300 px-3 py-2 text-sm text-slate-600 shadow-sm transition-all hover:border-slate-800 hover:bg-slate-800 hover:text-white hover:shadow-lg disabled:pointer-events-none disabled:opacity-50"
        onClick={() => goToPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        {showIcons ? <FaAngleRight size={16} /> : nextLabel}
      </button>
    </div>
  );
}
